import { featureFlag, useFeatureFlag } from '@routable/feature-configs';
import { createItemFormFields } from '@routable/shared';
import {} from '@routable/types';
import _cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUnmount } from 'react-use';
import { change } from 'redux-form';
import { formNamesItem } from 'constants/forms';
import { getInitialAnnotationLineItems } from 'helpers/ocr';
import { useSelectorWithProps } from 'hooks';
import { useCreateItemFormContext } from 'modules/dashboard/createItems/createItemForm/components/CreateItemForm/context';
import { createItemsTableViewModelManagerForKindSelector } from 'queries/tableCompoundSelectors';
import { createItemFormPurchaseOrderSelector } from 'selectors/forms';
import { consolidatedLineItemsSelector } from 'selectors/itemsSelectors';
import { INDIVIDUAL_LINE_ITEMS_DISABLED_TOOLTIP_TEXT, INDIVIDUAL_LINE_ITEMS_DISABLED_TOOLTIP_TEXT_SHORT, LINE_ITEM_MAX_COUNT, LINE_ITEMS_SECTION_NAME, SUMMARIZE_DISABLED_FOR_LINKED_PO_TOOLTIP_TEXT, } from './constants';
import { useAnnotationLineItems } from './useAnnotationLineItems';
export const useSummarizeLineItemsToggle = ({ isForcedConsolidatedLineItem, ocrAnnotation, }) => {
    const dispatch = useDispatch();
    const isSummarizeSeparateUIEnabled = useFeatureFlag(featureFlag.Enum.summarize_separate_ui_changes);
    const purchaseOrder = useSelector(createItemFormPurchaseOrderSelector);
    const hasPO = !!purchaseOrder?.value;
    const { isSummarized, isSummarizeInitialized, setIsSummarized, setIsSummarizeInitialized } = useCreateItemFormContext();
    const { annotationItems, annotationCount } = useAnnotationLineItems({});
    const viewModelManager = useSelector(createItemsTableViewModelManagerForKindSelector);
    const consolidatedLineItemsFromSelector = useSelectorWithProps(consolidatedLineItemsSelector, undefined);
    const consolidatedLineItemObj = consolidatedLineItemsFromSelector?.find((lineItem) => lineItem.value.id === ocrAnnotation?.id)?.value?.data || {};
    const viewModelManagerInitialValues = viewModelManager?.initialValues?.line_items;
    const unsummarizedFormValues = useMemo(() => getInitialAnnotationLineItems(_cloneDeep(viewModelManagerInitialValues || {}), annotationItems), [annotationItems, viewModelManagerInitialValues]);
    const consolidatedFormValues = useMemo(() => getInitialAnnotationLineItems(_cloneDeep(viewModelManagerInitialValues || {}), [consolidatedLineItemObj]), [consolidatedLineItemObj, viewModelManagerInitialValues]);
    const onHandleSummarize = useCallback(() => {
        const resultLineItems = isSummarized ? unsummarizedFormValues : consolidatedFormValues;
        dispatch(change(formNamesItem.CREATE_ITEM, LINE_ITEMS_SECTION_NAME, resultLineItems));
        setIsSummarized(!isSummarized);
        dispatch(change(formNamesItem.CREATE_ITEM, `${createItemFormFields.UI_FIELDS}.line_items`, {}));
        dispatch(change(formNamesItem.CREATE_ITEM, createItemFormFields.ITEM_IS_CONSOLIDATED, !isSummarized));
    }, [consolidatedFormValues, dispatch, isSummarized, setIsSummarized, unsummarizedFormValues]);
    const hasOver30LineItems = annotationCount > LINE_ITEM_MAX_COUNT;
    useEffect(() => {
        if (!isSummarizeInitialized && !hasOver30LineItems && !hasPO && isSummarizeSeparateUIEnabled) {
            setIsSummarizeInitialized(true);
            onHandleSummarize();
        }
    }, [hasOver30LineItems]);
    useEffect(() => {
        if (hasPO) {
            setIsSummarized(false);
        }
    }, [hasPO, setIsSummarized]);
    useUnmount(() => {
        setIsSummarizeInitialized(false);
        setIsSummarized(true);
    });
    let disabledTooltipText;
    if (hasPO) {
        disabledTooltipText = SUMMARIZE_DISABLED_FOR_LINKED_PO_TOOLTIP_TEXT;
    }
    else if (hasOver30LineItems) {
        disabledTooltipText = INDIVIDUAL_LINE_ITEMS_DISABLED_TOOLTIP_TEXT;
    }
    else if (isForcedConsolidatedLineItem) {
        disabledTooltipText = INDIVIDUAL_LINE_ITEMS_DISABLED_TOOLTIP_TEXT_SHORT;
    }
    return { disabledTooltipText, isSummarized, onHandleSummarize };
};
